
import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import axiosInstance from "../../utils/axiosInstance"
import { BASE_URL } from "../../config"
import { Fuel, Shield, DollarSign, Wrench, ArrowLeft, ImageIcon, Plus, Edit2, Trash2 } from "lucide-react"
import "./ViewMachine.css"

export default function ViewMachine() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [machine, setMachine] = useState(null)
  const [activeTab, setActiveTab] = useState("fuel")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Data states
  const [fuelRecords, setFuelRecords] = useState([])
  const [insuranceRecords, setInsuranceRecords] = useState([])
  const [emiRecords, setEmiRecords] = useState([])
  const [serviceRecords, setServiceRecords] = useState([])

  // Insurance CRUD states
  const [showInsuranceForm, setShowInsuranceForm] = useState(false)
  const [isEditInsurance, setIsEditInsurance] = useState(false)
  const [selectedInsurance, setSelectedInsurance] = useState(null)
  const [insuranceForm, setInsuranceForm] = useState({
    insuranceCompanyName: "",
    amount: "",
    startDate: "",
    endDate: "",
    insuranceType: "",
    forwhom: "",
    IDBvalue: "",
  })
  const [insuranceFiles, setInsuranceFiles] = useState({
    insuranceImage: null,
    rcImage: null,
    driverLicenseImage: null,
  })
  const [submittingInsurance, setSubmittingInsurance] = useState(false)
  const [deletingInsuranceId, setDeletingInsuranceId] = useState(null)

  // Service/Maintenance CRUD states
  const [showServiceForm, setShowServiceForm] = useState(false)
  const [isEditService, setIsEditService] = useState(false)
  const [selectedService, setSelectedService] = useState(null)
  const [serviceForm, setServiceForm] = useState({
    serviceDate: "",
    triggerValue: "",
    status: "Pending",
  })
  const [serviceBillImage, setServiceBillImage] = useState(null)
  const [submittingService, setSubmittingService] = useState(false)
  const [deletingServiceId, setDeletingServiceId] = useState(null)

  // EMI CRUD states
  const [showEmiForm, setShowEmiForm] = useState(false)
  const [isEditEmi, setIsEditEmi] = useState(false)
  const [selectedEmi, setSelectedEmi] = useState(null)
  const [emiForm, setEmiForm] = useState({
    emiAmount: "",
    emiDate: "",
    emiClosedDate: "",
  })
  const [submittingEmi, setSubmittingEmi] = useState(false)
  const [deletingEmiId, setDeletingEmiId] = useState(null)

  // Fuel CRUD states
  const [showFuelForm, setShowFuelForm] = useState(false)
  const [isEditFuel, setIsEditFuel] = useState(false)
  const [selectedFuelRecord, setSelectedFuelRecord] = useState(null)
  const [fuelForm, setFuelForm] = useState({
    fuelAdded: "",
    previousFuel: "",
    currentReading: "",
    previousReading: "",
    preWorkingHr: "",
    todayWorkingHr: "",
    date: "",
  })
  const [fuelFiles, setFuelFiles] = useState({
    currentReadingImg: null,
    dashBordImg: null,
    billImg: null,
  })
  const [submittingFuel, setSubmittingFuel] = useState(false)
  const [deletingFuelId, setDeletingFuelId] = useState(null)

  // Status popup states - UPDATED with new fields
  const [showStatusPopup, setShowStatusPopup] = useState(false)
  const [serviceStatus, setServiceStatus] = useState("Pending")
  const [billImage, setBillImage] = useState(null)
  const [submittingStatus, setSubmittingStatus] = useState(false)
  // New state variables for additional fields
  const [siteEngineerName, setSiteEngineerName] = useState("")
  const [driverName, setDriverName] = useState("")
  const [serviceEngineerName, setServiceEngineerName] = useState("")
  const [phoneNo, setPhoneNo] = useState("")

  // usageType logic
  const usageType = machine?.usageType || "PER_HOUR"
  const isPerKm = usageType === "PER_KM"

  // ── Helper ───────────────────────────────────────────────────────────────────

  const getToken = () => JSON.parse(localStorage.getItem("NagpurProperties"))?.token

  // ── Fetch All Data ───────────────────────────────────────────────────────────

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true)
        const headers = { Authorization: `Bearer ${getToken()}` }

        const [machineRes, fuelRes, insuranceRes, emiRes, serviceRes] = await Promise.all([
          axiosInstance.get(`${BASE_URL}/machines/${id}`, { headers }),
          axiosInstance.get(`${BASE_URL}/machines/machine/${id}`, { headers }),
          axiosInstance.get(`${BASE_URL}/insurance/by-machine/${id}`, { headers }),
          axiosInstance.get(`${BASE_URL}/emi/machine/${id}`, { headers }),
          axiosInstance.get(`${BASE_URL}/maintenance/by-machine/${id}`, { headers }),
        ])

        setMachine(machineRes.data)
        setFuelRecords(fuelRes.data || [])
        setInsuranceRecords(insuranceRes.data || [])
        setEmiRecords(emiRes.data || [])
        setServiceRecords(serviceRes.data || [])
      } catch (err) {
        console.error("Error fetching data:", err)
        setError("Failed to load machine details")
      } finally {
        setLoading(false)
      }
    }
    fetchAllData()
  }, [id])

  // ── Status Popup Handlers - UPDATED ──────────────────────────────────────────

  const openStatusPopup = (service) => {
    setSelectedService(service)
    setServiceStatus(service.status || "Pending")
    // Populate existing data if available
    setSiteEngineerName(service.siteEngineerName || "")
    setDriverName(service.driverName || "")
    setServiceEngineerName(service.serviceEngineerName || "")
    setPhoneNo(service.phoneNo || "")
    setBillImage(null)
    setShowStatusPopup(true)
  }

  const handleStatusUpdate = async () => {
    if (!selectedService) return
    
    // Validation
    if (!siteEngineerName.trim()) {
      alert("Please enter site engineer name")
      return
    }
    if (!driverName.trim()) {
      alert("Please enter driver name")
      return
    }
    if (!serviceEngineerName.trim()) {
      alert("Please enter service engineer name")
      return
    }
    if (!phoneNo.trim()) {
      alert("Please enter phone number")
      return
    }
    
    const formData = new FormData()
    if (billImage) {
      formData.append("billimage", billImage)
    }
    
    try {
      setSubmittingStatus(true)
      
      // Build URL with query parameters including all required fields
      const queryParams = new URLSearchParams({
        status: serviceStatus,
        siteEngineerName: siteEngineerName,
        driverName: driverName,
        serviceEngineerName: serviceEngineerName,
        phoneNo: phoneNo,
      }).toString()
      
      await axiosInstance({
        method: "put",
        url: `${BASE_URL}/maintenance/${selectedService.id}/status?${queryParams}`,
        data: formData,
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "multipart/form-data",
        },
      })
      
      setShowStatusPopup(false)
      
      // Reset form fields
      setSiteEngineerName("")
      setDriverName("")
      setServiceEngineerName("")
      setPhoneNo("")
      setServiceStatus("Pending")
      setBillImage(null)
      
      // Refresh service records
      const res = await axiosInstance.get(`${BASE_URL}/maintenance/by-machine/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      setServiceRecords(res.data || [])
      alert("Status updated successfully!")
    } catch (err) {
      console.error("Failed to update status", err)
      alert("Failed to update status: " + (err.response?.data?.message || "Unknown error"))
    } finally {
      setSubmittingStatus(false)
    }
  }

  // ── Fuel Handlers ────────────────────────────────────────────────────────────

  const handleFuelChange = (e) => {
    const { name, value } = e.target
    setFuelForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleFuelFileChange = (e) => {
    const { name, files } = e.target
    setFuelFiles((prev) => ({ ...prev, [name]: files[0] }))
  }

  const openAddFuel = () => {
    setIsEditFuel(false)
    setSelectedFuelRecord(null)
    setFuelForm({
      fuelAdded: "",
      previousFuel: "",
      currentReading: "",
      previousReading: "",
      preWorkingHr: "",
      todayWorkingHr: "",
      date: new Date().toISOString().split("T")[0],
    })
    setFuelFiles({ currentReadingImg: null, dashBordImg: null, billImg: null })
    setShowFuelForm(true)
  }

  const openEditFuel = async (record) => {
    setIsEditFuel(true)
    setSelectedFuelRecord(record)
    try {
      const res = await axiosInstance.get(`${BASE_URL}/machines/fuel/${record.id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      const data = res.data
      setFuelForm({
        fuelAdded: data.fuelAdded?.toString() || "",
        previousFuel: data.previousFuel?.toString() || "",
        currentReading: data.currentReading?.toString() || "",
        previousReading: data.previousReading?.toString() || "",
        preWorkingHr: data.preWorkingHr?.toString() || "",
        todayWorkingHr: data.totalworkingHr?.toString() || "",
        date: data.date || new Date().toISOString().split("T")[0],
      })
    } catch (err) {
      console.error("Failed to fetch fuel record, using list data:", err)
      setFuelForm({
        fuelAdded: record.fuelAdded?.toString() || "",
        previousFuel: record.previousFuel?.toString() || "",
        currentReading: record.currentReading?.toString() || "",
        previousReading: record.previousReading?.toString() || "",
        preWorkingHr: record.preWorkingHr?.toString() || "",
        todayWorkingHr: record.totalworkingHr?.toString() || "",
        date: record.date || new Date().toISOString().split("T")[0],
      })
    }
    setFuelFiles({ currentReadingImg: null, dashBordImg: null, billImg: null })
    setShowFuelForm(true)
  }

  const handleDeleteFuel = async (usageId) => {
    if (!window.confirm("Are you sure you want to delete this fuel record?")) return
    setDeletingFuelId(usageId)
    try {
      await axiosInstance.delete(`${BASE_URL}/machines/fuel/${usageId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      const res = await axiosInstance.get(`${BASE_URL}/machines/machine/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      setFuelRecords(res.data || [])
      alert("Fuel record deleted successfully")
    } catch (err) {
      alert("Failed to delete fuel record: " + (err.response?.data?.message || "Unknown error"))
      console.error(err)
    } finally {
      setDeletingFuelId(null)
    }
  }

  const handleSubmitFuel = async (e) => {
    e.preventDefault()
    
    const preWr = parseFloat(fuelForm.preWorkingHr) || 0
    const todayWr = parseFloat(fuelForm.todayWorkingHr) || 0

    if (todayWr > 0 && preWr > todayWr) {
      alert("Previous working hours cannot exceed today's total working hours.")
      return
    }

    setSubmittingFuel(true)
    try {
      const formData = new FormData()

      const dataPayload = {
        vehicleId: parseInt(id),
        fuelAdded: parseFloat(fuelForm.fuelAdded) || 0,
        previousFuel: parseFloat(fuelForm.previousFuel) || 0,
        date: fuelForm.date,
        currentReading: parseFloat(fuelForm.currentReading) || 0,
        previousReading: parseFloat(fuelForm.previousReading) || 0,
        preWorkingHr: preWr,
        todayWorkingHr: todayWr,
      }

      formData.append(
        "data",
        new Blob([JSON.stringify(dataPayload)], { type: "application/json" })
      )

      if (fuelFiles.currentReadingImg) formData.append("currentReadingImg", fuelFiles.currentReadingImg)
      if (fuelFiles.dashBordImg) formData.append("dashBordImg", fuelFiles.dashBordImg)
      if (fuelFiles.billImg) formData.append("billImg", fuelFiles.billImg)

      const url = isEditFuel
        ? `${BASE_URL}/machines/fuleupdate/${selectedFuelRecord.id}`
        : `${BASE_URL}/machines/create`

      await axiosInstance({
        method: isEditFuel ? "put" : "post",
        url,
        data: formData,
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "multipart/form-data",
        },
      })

      const res = await axiosInstance.get(`${BASE_URL}/machines/machine/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      setFuelRecords(res.data || [])
      alert(isEditFuel ? "Fuel record updated successfully!" : "Fuel entry recorded successfully!")
      setShowFuelForm(false)
    } catch (err) {
      console.error("Fuel operation error:", err)
      alert("Failed to save fuel record: " + (err.response?.data?.message || "Unknown error"))
    } finally {
      setSubmittingFuel(false)
    }
  }

  // ── Insurance Handlers ───────────────────────────────────────────────────────

  const handleInsuranceChange = (e) => {
    const { name, value } = e.target
    setInsuranceForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleInsuranceFileChange = (e) => {
    const { name, files } = e.target
    setInsuranceFiles((prev) => ({ ...prev, [name]: files[0] }))
  }

  const openAddInsurance = () => {
    setIsEditInsurance(false)
    setSelectedInsurance(null)
    setInsuranceForm({
      insuranceCompanyName: "",
      amount: "",
      startDate: "",
      endDate: "",
      insuranceType: "",
      forwhom: "",
      IDBvalue: "",
    })
    setInsuranceFiles({ insuranceImage: null, rcImage: null, driverLicenseImage: null })
    setShowInsuranceForm(true)
  }

  const openEditInsurance = (record) => {
    setIsEditInsurance(true)
    setSelectedInsurance(record)
    setInsuranceForm({
      insuranceCompanyName: record.insuranceCompanyName || "",
      amount: record.amount?.toString() || "",
      startDate: record.startDate ? record.startDate.split("T")[0] : "",
      endDate: record.endDate ? record.endDate.split("T")[0] : "",
      insuranceType: record.insuranceType || "",
      forwhom: record.forwhom || "",
      IDBvalue: record.idbvalue || "",
    })
    setInsuranceFiles({ insuranceImage: null, rcImage: null, driverLicenseImage: null })
    setShowInsuranceForm(true)
  }

  const handleDeleteInsurance = async (insuranceId) => {
    if (!window.confirm("Are you sure you want to delete this insurance record?")) return
    setDeletingInsuranceId(insuranceId)
    try {
      await axiosInstance.delete(`${BASE_URL}/insurance/delete/${insuranceId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      const res = await axiosInstance.get(`${BASE_URL}/insurance/by-machine/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      setInsuranceRecords(res.data || [])
      alert("Insurance record deleted successfully")
    } catch (err) {
      alert("Failed to delete insurance record: " + (err.response?.data?.message || "Unknown error"))
      console.error(err)
    } finally {
      setDeletingInsuranceId(null)
    }
  }

  const handleSubmitInsurance = async (e) => {
    e.preventDefault()
    setSubmittingInsurance(true)
    try {
      const queryParams = new URLSearchParams({
        mechineName: machine?.machineName || "",
        insuranceCompanyName: insuranceForm.insuranceCompanyName,
        amount: insuranceForm.amount,
        startDate: insuranceForm.startDate,
        endDate: insuranceForm.endDate,
        insuranceType: insuranceForm.insuranceType,
        forwhom: insuranceForm.forwhom,
        machineId: id,
        IDBvalue: insuranceForm.IDBvalue || "",
      }).toString()

      const formData = new FormData()
      if (insuranceFiles.insuranceImage) formData.append("insuranceImage", insuranceFiles.insuranceImage)
      if (insuranceFiles.rcImage) formData.append("rcImage", insuranceFiles.rcImage)
      if (insuranceFiles.driverLicenseImage) formData.append("driverLicenseImage", insuranceFiles.driverLicenseImage)

      const baseUrl = isEditInsurance
        ? `${BASE_URL}/insurance/update/${selectedInsurance.id}`
        : `${BASE_URL}/insurance/create`

      await axiosInstance({
        method: isEditInsurance ? "put" : "post",
        url: `${baseUrl}?${queryParams}`,
        data: formData,
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "multipart/form-data",
        },
      })

      const res = await axiosInstance.get(`${BASE_URL}/insurance/by-machine/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      setInsuranceRecords(res.data || [])
      setShowInsuranceForm(false)
      alert(isEditInsurance ? "Insurance updated successfully!" : "Insurance added successfully!")
    } catch (err) {
      alert("Failed to save insurance record: " + (err.response?.data?.message || "Unknown error"))
      console.error(err)
    } finally {
      setSubmittingInsurance(false)
    }
  }

  // ── Maintenance Handlers ─────────────────────────────────────────────────────

  const handleServiceChange = (e) => {
    const { name, value } = e.target
    setServiceForm((prev) => ({ ...prev, [name]: value }))
  }

  const openAddService = () => {
    setIsEditService(false)
    setSelectedService(null)
    setServiceForm({ serviceDate: "", triggerValue: "", status: "Pending" })
    setServiceBillImage(null)
    setShowServiceForm(true)
  }

  const openEditService = (record) => {
    setIsEditService(true)
    setSelectedService(record)
    setServiceForm({
      serviceDate: record.serviceDate ? record.serviceDate.split("T")[0] : "",
      triggerValue: record.triggerValue?.toString() || "",
      status: record.status || "Pending",
    })
    setServiceBillImage(null)
    setShowServiceForm(true)
  }

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm("Are you sure you want to delete this maintenance record?")) return
    setDeletingServiceId(serviceId)
    try {
      await axiosInstance.delete(`${BASE_URL}/maintenance/delete/${serviceId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      const res = await axiosInstance.get(`${BASE_URL}/maintenance/by-machine/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      setServiceRecords(res.data || [])
      alert("Service record deleted successfully")
    } catch (err) {
      alert("Failed to delete service record: " + (err.response?.data?.message || "Unknown error"))
      console.error(err)
    } finally {
      setDeletingServiceId(null)
    }
  }

  const handleSubmitService = async (e) => {
    e.preventDefault()
    setSubmittingService(true)
    try {
      const queryParams = new URLSearchParams({
        machineId: id,
        triggerValue: serviceForm.triggerValue,
        usageType: usageType,
        serviceDate: serviceForm.serviceDate || "",
        status: serviceForm.status,
      }).toString()

      const baseUrl = isEditService
        ? `${BASE_URL}/maintenance/update/${selectedService.id}`
        : `${BASE_URL}/maintenance/add`

      const formData = new FormData()
      if (serviceBillImage) {
        formData.append("billimage", serviceBillImage)
      }

      await axiosInstance({
        method: isEditService ? "put" : "post",
        url: `${baseUrl}?${queryParams}`,
        data: formData,
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "multipart/form-data",
        },
      })

      const res = await axiosInstance.get(`${BASE_URL}/maintenance/by-machine/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      setServiceRecords(res.data || [])
      setShowServiceForm(false)
      alert(isEditService ? "Service updated successfully!" : "Service added successfully!")
    } catch (err) {
      console.error("Maintenance save error:", err)
      alert("Failed to save service record: " + (err.response?.data?.message || "Unknown error"))
    } finally {
      setSubmittingService(false)
    }
  }

  // ── EMI Handlers ─────────────────────────────────────────────────────────────

  const handleEmiChange = (e) => {
    const { name, value } = e.target
    setEmiForm((prev) => ({ ...prev, [name]: value }))
  }

  const openAddEmi = () => {
    setIsEditEmi(false)
    setSelectedEmi(null)
    setEmiForm({ emiAmount: "", emiDate: "", emiClosedDate: "" })
    setShowEmiForm(true)
  }

  const openEditEmi = (record) => {
    setIsEditEmi(true)
    setSelectedEmi(record)
    setEmiForm({
      emiAmount: record.emiAmount?.toString() || "",
      emiDate: record.emiDate ? record.emiDate.split("T")[0] : "",
      emiClosedDate: record.emiClosedDate ? record.emiClosedDate.split("T")[0] : "",
    })
    setShowEmiForm(true)
  }

  const handleDeleteEmi = async (emiId) => {
    if (!window.confirm("Are you sure you want to delete this EMI record?")) return
    setDeletingEmiId(emiId)
    try {
      await axiosInstance.delete(`${BASE_URL}/emi/delete/${emiId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      const res = await axiosInstance.get(`${BASE_URL}/emi/machine/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      setEmiRecords(res.data || [])
      alert("EMI record deleted successfully")
    } catch (err) {
      alert("Failed to delete EMI record: " + (err.response?.data?.message || "Unknown error"))
      console.error(err)
    } finally {
      setDeletingEmiId(null)
    }
  }

  const handleSubmitEmi = async (e) => {
    e.preventDefault()
    setSubmittingEmi(true)
    try {
      const payload = {
        emiAmount: parseFloat(emiForm.emiAmount),
        emiDate: emiForm.emiDate,
        emiClosedDate: emiForm.emiClosedDate || null,
        machineId: parseInt(id),
      }

      const url = isEditEmi
        ? `${BASE_URL}/emi/update/${selectedEmi.id}`
        : `${BASE_URL}/emi/addEmi/${id}`

      await axiosInstance({
        method: isEditEmi ? "put" : "post",
        url,
        data: payload,
        headers: { Authorization: `Bearer ${getToken()}` },
      })

      const res = await axiosInstance.get(`${BASE_URL}/emi/machine/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      setEmiRecords(res.data || [])
      setShowEmiForm(false)
      alert(isEditEmi ? "EMI updated successfully!" : "EMI added successfully!")
    } catch (err) {
      console.error("EMI save error:", err)
      alert("Failed to save EMI: " + (err.response?.data?.message || "Unknown error"))
    } finally {
      setSubmittingEmi(false)
    }
  }

  // ── RENDER ───────────────────────────────────────────────────────────────────

  if (loading) return <div className="loading-screen">Loading machine details...</div>
  if (error) return <div className="error-screen">{error}</div>
  if (!machine) return <div className="not-found">Machine not found</div>

  return (
    <div className="view-machine-page">
      <header className="page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} /> Back to Fleet
        </button>
        <div className="header-main">
          <div className="header-info">
            <h1>{machine.machineName}</h1>
            <p className="machine-subtitle">
              {machine.manufacturer} {machine.model} • {machine.mechinetype}
            </p>
          </div>
        </div>
      </header>

      <div className="tabs-container">
        <button className={`tab-item ${activeTab === "fuel" ? "active" : ""}`} onClick={() => setActiveTab("fuel")}>
          <Fuel size={18} /> Fuel
        </button>
        <button
          className={`tab-item ${activeTab === "insurance" ? "active" : ""}`}
          onClick={() => setActiveTab("insurance")}
        >
          <Shield size={18} /> Insurance
        </button>
        <button className={`tab-item ${activeTab === "emi" ? "active" : ""}`} onClick={() => setActiveTab("emi")}>
          <DollarSign size={18} /> EMI
        </button>
        <button
          className={`tab-item ${activeTab === "service" ? "active" : ""}`}
          onClick={() => setActiveTab("service")}
        >
          <Wrench size={18} /> Maintenance
        </button>
      </div>

      <main className="tab-content">

        {/* ── FUEL TAB ── */}
        {activeTab === "fuel" && (
          <div className="tab-section">
            <div className="section-header">
              <h2>Fuel & Usage History</h2>
              <button className="add-button" onClick={openAddFuel}>
                <Plus size={18} /> Record Fuel
              </button>
            </div>

            <div className="summary-cards">
              <div className="summary-card">
                <div className="card-icon"><Fuel size={20} /></div>
                <div className="card-content">
                  <h3>Fuel Capacity</h3>
                  <p className="big-value">{machine.fuleCapacity || 0} L</p>
                </div>
              </div>
              <div className="summary-card">
                <div className="card-icon"><Fuel size={20} /></div>
                <div className="card-content">
                  <h3>Average</h3>
                  <p className="big-value">
                    {machine?.kmPerLiter != null
                      ? `${Number(machine.kmPerLiter).toFixed(1)} KM/L`
                      : machine?.literPerHour != null
                        ? `${Number(machine.literPerHour).toFixed(1)} L/HR`
                        : "—"}
                  </p>
                </div>
              </div>
            </div>

            <div className="section-header">
              <h3>Recent Telemetry</h3>
            </div>

            <div className="fuel-table-wrapper">
              <table className="data-table" style={{ minWidth: "1100px", width: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ minWidth: "100px" }}>Date</th>
                    <th style={{ minWidth: "110px" }}>Fuel Added (L)</th>
                    <th style={{ minWidth: "100px" }}>Prev Fuel (L)</th>
                    <th style={{ minWidth: "110px" }}>Curr Reading</th>
                    <th style={{ minWidth: "110px" }}>Prev Reading</th>
                    <th style={{ minWidth: "110px" }}>Used Reading</th>
                    <th style={{ minWidth: "130px" }}>Total Work Hrs</th>
                    <th style={{ minWidth: "100px" }}>Difference</th>
                    <th style={{ minWidth: "150px" }}>Images</th>
                    <th style={{ minWidth: "150px" }}>Status</th>

                    <th style={{ minWidth: "90px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {fuelRecords.map((r) => (
                    <tr key={r.id}>
                      <td>{r.date || "—"}</td>
                      <td>
                        <span className="fuel-added-badge">+{r.fuelAdded ?? "—"}</span>
                      </td>
                      <td>{r.previousFuel ?? "—"}</td>
                      <td>{r.currentReading ?? "—"}</td>
                      <td>{r.previousReading ?? "—"}</td>
                      <td>{r.useReading ?? "—"}</td>
                      <td>{r.totalworkingHr ?? "—"}</td>
                      <td>
                        {r.difference != null ? (
                          <span className={r.difference >= 0 ? "diff-positive" : "diff-negative"}>
                            {r.difference >= 0 ? "+" : ""}{r.difference}
                          </span>
                        ) : "—"}
                       </td>
                      <td>
                        <div className="document-links">
                          {r.currentReadingImg && (
                            <a href={r.currentReadingImg} target="_blank" rel="noreferrer" className="doc-link">
                              <ImageIcon size={13} /> Reading
                            </a>
                          )}
                          {r.dashBordImg && (
                            <a href={r.dashBordImg} target="_blank" rel="noreferrer" className="doc-link">
                              <ImageIcon size={13} /> Dash
                            </a>
                          )}
                          {r.billImg && (
                            <a href={r.billImg} target="_blank" rel="noreferrer" className="doc-link">
                              <ImageIcon size={13} /> Bill
                            </a>
                          )}
                        </div>
                       </td>
                     <td>
  <span className={`status-badge ${r.status?.toLowerCase().trim() === "normal" ? "status-normal" : "status-different"}`}>
    {r.status ?? "—"}
  </span>
</td>
                      <td className="actions-cell">
                        <button className="action-btn edit-btn" onClick={() => openEditFuel(r)}>
                          <Edit2 size={15} />
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={() => handleDeleteFuel(r.id)}
                          disabled={deletingFuelId === r.id}
                        >
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {fuelRecords.length === 0 && (
                <div className="empty-state">No fuel records found.</div>
              )}
            </div>
          </div>
        )}

        {/* ── INSURANCE TAB ── */}
        {activeTab === "insurance" && (
          <div className="tab-section">
            <div className="section-header">
              <h2>Active Coverage</h2>
              <button className="add-button" onClick={openAddInsurance}>
                <Plus size={18} /> New Record
              </button>
            </div>
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>Type</th>
                    <th>For Whom</th>
                    <th>IDB Value</th>
                    <th>Validity</th>
                    <th>Status</th>
                    <th>Docs</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {insuranceRecords.map((ins) => {
                    const isActive = new Date(ins.endDate) > new Date()
                    return (
                      <tr key={ins.id}>
                        <td>{ins.insuranceCompanyName}</td>
                        <td>{ins.insuranceType}</td>
                        <td>{ins.forwhom || "—"}</td>
                        <td>{ins.idbvalue || "—"}</td>
                        <td>{ins.startDate} — {ins.endDate}</td>
                        <td>
                          <span className={`status-badge ${isActive ? "active" : "expired"}`}>
                            {isActive ? "Active" : "Expired"}
                          </span>
                        </td>
                        <td>
                          <div className="document-links">
                            {ins.images && (
                              <a href={ins.images} target="_blank" className="doc-link" rel="noreferrer">
                                <ImageIcon size={14} /> Ins
                              </a>
                            )}
                            {ins.rcimage && (
                              <a href={ins.rcimage} target="_blank" className="doc-link" rel="noreferrer">
                                <ImageIcon size={14} /> RC
                              </a>
                            )}
                            {ins.driverlicenceimage && (
                              <a href={ins.driverlicenceimage} target="_blank" className="doc-link" rel="noreferrer">
                                <ImageIcon size={14} /> DL
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="actions-cell">
                          <button className="action-btn edit-btn" onClick={() => openEditInsurance(ins)}>
                            <Edit2 size={16} />
                          </button>
                          <button
                            className="action-btn delete-btn"
                            onClick={() => handleDeleteInsurance(ins.id)}
                            disabled={deletingInsuranceId === ins.id}
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              {insuranceRecords.length === 0 && <div className="empty-state">No insurance records found.</div>}
            </div>
          </div>
        )}

        {/* ── EMI TAB ── */}
        {activeTab === "emi" && (
          <div className="tab-section">
            <div className="section-header">
              <h2>Financial Obligations</h2>
              <button className="add-button" onClick={openAddEmi}>
                <Plus size={18} /> New EMI
              </button>
            </div>
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Due Date</th>
                    <th>Installment</th>
                    <th>Payment Status</th>
                    <th>Settlement Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {emiRecords.map((emi) => (
                    <tr key={emi.id}>
                      <td>{emi.emiDate || "—"}</td>
                      <td>₹{Number(emi.emiAmount || 0).toLocaleString()}</td>
                      <td>
                        <span className={`status-badge ${emi.emiClosedDate ? "paid" : "pending"}`}>
                          {emi.emiClosedDate ? "Cleared" : "Pending"}
                        </span>
                      </td>
                      <td>{emi.emiClosedDate || "—"}</td>
                      <td className="actions-cell">
                        <button className="action-btn edit-btn" onClick={() => openEditEmi(emi)}>
                          <Edit2 size={16} />
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={() => handleDeleteEmi(emi.id)}
                          disabled={deletingEmiId === emi.id}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {emiRecords.length === 0 && <div className="empty-state">No EMI records found.</div>}
            </div>
          </div>
        )}

        {/* ── SERVICE TAB - UPDATED with new columns ── */}
        {activeTab === "service" && (
          <div className="tab-section">
            <div className="section-header">
              <h2>Maintenance Logs</h2>
              <button className="add-button" onClick={openAddService}>
                <Plus size={18} /> New Log
              </button>
            </div>
            <div style={{ fontSize: "0.9rem", color: "#666", margin: "12px 0 20px" }}>
              <strong>Trigger value unit:</strong> {usageType === "PER_KM" ? "Kilometers" : "Operating Hours"}
            </div>
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Log Date</th>
                    <th>Condition</th>
                    <th>Metric Value</th>
                    <th>Site Engineer</th>
                    <th>Driver Name</th>
                    <th>Service Engineer</th>
                    <th>Phone No</th>
                    <th>Workflow Status</th>
                    <th>Bill Image</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {serviceRecords.map((s) => (
                    <tr key={s.id}>
                      <td>{s.serviceDate || "—"}</td>
                      <td>{s.machineModel || "—"}</td>
                      <td>{s.triggerValue || "—"}</td>
                      <td>{s.siteEngineerName || "—"}</td>
                      <td>{s.driverName || "—"}</td>
                      <td>{s.serviceEngineerName || "—"}</td>
                      <td>{s.phoneNo || "—"}</td>
                      <td>
                        <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                          <span
                            className={`status-badge ${s.status?.toLowerCase() === "completed" ? "active" : "pending"}`}
                          >
                            {s.status || "Unknown"}
                          </span>
                          <button
                            style={{
                              backgroundColor: "#545492",
                              color: "white",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              border: "none",
                              cursor: "pointer",
                              fontSize: "12px",
                            }}
                            onClick={() => openStatusPopup(s)}
                          >
                            Update Status
                          </button>
                        </div>
                      </td>
                      <td>
                        {s.billimage && (
                          <a href={s.billimage} target="_blank" className="doc-link" rel="noreferrer">
                            <ImageIcon size={14} /> Bill
                          </a>
                        )}
                      </td>
                      <td className="actions-cell">
                        <button className="action-btn edit-btn" onClick={() => openEditService(s)}>
                          <Edit2 size={16} />
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={() => handleDeleteService(s.id)}
                          disabled={deletingServiceId === s.id}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {serviceRecords.length === 0 && <div className="empty-state">No maintenance history recorded.</div>}
            </div>
          </div>
        )}
      </main>

      {/* ── FUEL MODAL ── */}
      {showFuelForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{isEditFuel ? "Edit Fuel Record" : "Record Daily Fuel & Usage"}</h3>
              <button className="close-modal-btn" onClick={() => setShowFuelForm(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmitFuel}>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Date *</label>
                    <input type="date" name="date" value={fuelForm.date} onChange={handleFuelChange} required />
                  </div>
                  <div className="form-group">
                    <label>Fuel Added (L) *</label>
                    <input
                      type="number" name="fuelAdded" value={fuelForm.fuelAdded}
                      onChange={handleFuelChange} step="0.01" min="0" placeholder="e.g. 45.5" required
                    />
                  </div>
                  <div className="form-group">
                    <label>Previous Fuel (L)</label>
                    <input
                      type="number" name="previousFuel" value={fuelForm.previousFuel}
                      onChange={handleFuelChange} step="0.01" min="0" placeholder="e.g. 10.0"
                    />
                  </div>
                  <div className="form-group">
                    <label>Current Reading ({isPerKm ? "KM" : "HR"})</label>
                    <input
                      type="number" name="currentReading" value={fuelForm.currentReading}
                      onChange={handleFuelChange} step="0.1" min="0"
                      placeholder={isPerKm ? "e.g. 12500" : "e.g. 540"}
                    />
                  </div>
                  <div className="form-group">
                    <label>Previous Reading ({isPerKm ? "KM" : "HR"})</label>
                    <input
                      type="number" name="previousReading" value={fuelForm.previousReading}
                      onChange={handleFuelChange} step="0.1" min="0"
                      placeholder={isPerKm ? "e.g. 12380" : "e.g. 533"}
                    />
                  </div>
                  <div className="form-group">
                    <label>Previous Working Hours</label>
                    <input
                      type="number" name="preWorkingHr" value={fuelForm.preWorkingHr}
                      onChange={handleFuelChange} step="0.1" min="0" placeholder="e.g. 6.0"
                    />
                  </div>
                  <div className="form-group">
                    <label>Today Working Hours</label>
                    <input
                      type="number" name="todayWorkingHr" value={fuelForm.todayWorkingHr}
                      onChange={handleFuelChange} step="0.1" min="0" placeholder="e.g. 7.5"
                    />
                  </div>
                  <div className="form-group">
                    <label>Current Reading Image</label>
                    <input type="file" name="currentReadingImg" accept="image/*" onChange={handleFuelFileChange} />
                  </div>
                  <div className="form-group">
                    <label>Dashboard Image</label>
                    <input type="file" name="dashBordImg" accept="image/*" onChange={handleFuelFileChange} />
                  </div>
                  <div className="form-group">
                    <label>Bill Image</label>
                    <input type="file" name="billImg" accept="image/*" onChange={handleFuelFileChange} />
                  </div>
                </div>
                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={() => setShowFuelForm(false)}>Cancel</button>
                  <button type="submit" className="submit-btn" disabled={submittingFuel}>
                    {submittingFuel ? "Processing..." : isEditFuel ? "Update Record" : "Record Entry"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── INSURANCE MODAL ── */}
      {showInsuranceForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{isEditInsurance ? "Modify Insurance Record" : "Register Insurance"}</h3>
              <button className="close-modal-btn" onClick={() => setShowInsuranceForm(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmitInsurance}>
                <div className="form-grid">
                  <div className="form-group full">
                    <label>Provider Agency *</label>
                    <input
                      type="text" name="insuranceCompanyName" value={insuranceForm.insuranceCompanyName}
                      onChange={handleInsuranceChange} required placeholder="e.g. LIC, HDFC Ergo"
                    />
                  </div>
                  <div className="form-group">
                    <label>Premium Amount (₹) *</label>
                    <input
                      type="number" name="amount" value={insuranceForm.amount}
                      onChange={handleInsuranceChange} required placeholder="e.g. 25000"
                    />
                  </div>
                  <div className="form-group">
                    <label>Coverage Type *</label>
                    <select name="insuranceType" value={insuranceForm.insuranceType} onChange={handleInsuranceChange} required>
                      <option value="">Select Type</option>
                      <option value="Comprehensive">Comprehensive</option>
                      <option value="Third Party">Third Party</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Activation Date *</label>
                    <input type="date" name="startDate" value={insuranceForm.startDate} onChange={handleInsuranceChange} required />
                  </div>
                  <div className="form-group">
                    <label>Expiry Date *</label>
                    <input type="date" name="endDate" value={insuranceForm.endDate} onChange={handleInsuranceChange} required />
                  </div>
                  <div className="form-group">
                    <label>For Whom *</label>
                    <input
                      type="text" name="forwhom" value={insuranceForm.forwhom}
                      onChange={handleInsuranceChange} required placeholder="e.g. Driver / Owner"
                    />
                  </div>
                  <div className="form-group">
                    <label>IDB Value</label>
                    <input
                      type="text" name="IDBvalue" value={insuranceForm.IDBvalue}
                      onChange={handleInsuranceChange} placeholder="e.g. IDB123"
                    />
                  </div>
                  <div className="form-group">
                    <label>Insurance Image</label>
                    <input type="file" name="insuranceImage" accept="image/*" onChange={handleInsuranceFileChange} />
                  </div>
                  <div className="form-group">
                    <label>RC Image</label>
                    <input type="file" name="rcImage" accept="image/*" onChange={handleInsuranceFileChange} />
                  </div>
                  <div className="form-group">
                    <label>Driver License Image</label>
                    <input type="file" name="driverLicenseImage" accept="image/*" onChange={handleInsuranceFileChange} />
                  </div>
                </div>
                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={() => setShowInsuranceForm(false)}>Dismiss</button>
                  <button type="submit" className="submit-btn" disabled={submittingInsurance}>
                    {submittingInsurance ? "Processing..." : "Secure Record"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── SERVICE MODAL ── */}
      {showServiceForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{isEditService ? "Update Maintenance Log" : "New Service Entry"}</h3>
              <button className="close-modal-btn" onClick={() => setShowServiceForm(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmitService}>
                <div className="form-group">
                  <label>Service Date</label>
                  <input
                    type="date" name="serviceDate" value={serviceForm.serviceDate}
                    onChange={handleServiceChange} required
                  />
                </div>
                <div className="form-group">
                  <label>Trigger Value ({usageType === "PER_KM" ? "kilometers" : "operating hours"})</label>
                  <input
                    type="number" name="triggerValue" value={serviceForm.triggerValue}
                    onChange={handleServiceChange} required
                    placeholder={usageType === "PER_KM" ? "e.g. 5000" : "e.g. 250"} step="any"
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select name="status" value={serviceForm.status} onChange={handleServiceChange} required>
                    <option value="Pending">Pending</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Bill Image (optional)</label>
                  <input type="file" accept="image/*" onChange={(e) => setServiceBillImage(e.target.files[0])} />
                </div>
                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={() => setShowServiceForm(false)}>Dismiss</button>
                  <button type="submit" className="submit-btn" disabled={submittingService}>
                    {submittingService ? "Syncing..." : "Log Maintenance"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── EMI MODAL ── */}
      {showEmiForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{isEditEmi ? "Update EMI Record" : "Add New EMI"}</h3>
              <button className="close-modal-btn" onClick={() => setShowEmiForm(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmitEmi}>
                <div className="form-group">
                  <label>Installment Amount (₹)</label>
                  <input
                    type="number" name="emiAmount" value={emiForm.emiAmount}
                    onChange={handleEmiChange} step="0.01" min="0" required placeholder="e.g. 24500.50"
                  />
                </div>
                <div className="form-group">
                  <label>Due Date</label>
                  <input type="date" name="emiDate" value={emiForm.emiDate} onChange={handleEmiChange} required />
                </div>
                <div className="form-group">
                  <label>Settlement / Payment Date (optional)</label>
                  <input type="date" name="emiClosedDate" value={emiForm.emiClosedDate} onChange={handleEmiChange} />
                </div>
                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={() => setShowEmiForm(false)}>Dismiss</button>
                  <button type="submit" className="submit-btn" disabled={submittingEmi}>
                    {submittingEmi ? "Processing..." : isEditEmi ? "Update EMI" : "Add EMI"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── STATUS POPUP MODAL - UPDATED with new fields ── */}
      {showStatusPopup && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: "500px" }}>
            <div className="modal-header">
              <h3>Update Service Status</h3>
              <button className="close-modal-btn" onClick={() => setShowStatusPopup(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => { e.preventDefault(); handleStatusUpdate(); }}>
                <div className="form-group">
                  <label>Status *</label>
                  <select 
                    value={serviceStatus} 
                    onChange={(e) => setServiceStatus(e.target.value)}
                    required
                  >
                    <option value="Pending">Pending</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Site Engineer Name *</label>
                  <input 
                    type="text"
                    value={siteEngineerName}
                    onChange={(e) => setSiteEngineerName(e.target.value)}
                    placeholder="Enter site engineer name"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Driver Name *</label>
                  <input 
                    type="text"
                    value={driverName}
                    onChange={(e) => setDriverName(e.target.value)}
                    placeholder="Enter driver name"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Service Engineer Name *</label>
                  <input 
                    type="text"
                    value={serviceEngineerName}
                    onChange={(e) => setServiceEngineerName(e.target.value)}
                    placeholder="Enter service engineer name"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input 
                    type="tel"
                    value={phoneNo}
                    onChange={(e) => setPhoneNo(e.target.value)}
                    placeholder="Enter phone number"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Bill Image (optional)</label>
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={(e) => setBillImage(e.target.files[0])} 
                  />
                </div>
                
                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={() => setShowStatusPopup(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="submit-btn" disabled={submittingStatus}>
                    {submittingStatus ? "Updating..." : "Update Status"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}